import React, { FC, useContext, useMemo } from 'react'
import NoResultPage from '../../../../components/NoResultPage'
import SpringDataTableWithLoader from '../../../../components/layout/SpringDataTableWithLoader'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { EXPORT_SORTIE_FORM, SORTIES_DATATABLE, UPDATE_COMMENTAIRE_SORTIE_FORM } from '../services/sortie.consts'
import { ExportSortieForm, QuantiteUcDisplayable, Sortie, SortieCommentaireForm } from '../services/sortie.types'
import { dataTableActions, getStyles } from 'isotope-client'
import withStyles from '@material-ui/core/styles/withStyles'
import classnames from 'classnames'
import { formatDeboucheLabel } from '../services/sortie.utils'
import { LocalDateTime } from '../../../../utils/date/local-date-time'
import { compose } from 'redux'
import { injectTypeConditionnementValueList } from '../../../common/valueLists/valueListInjectors'
import { DataTableHeader, ValueListEntry } from '../../../../utils/types/global.types'
import { DialogContext } from '../../../../components/layout/dialog'
import { colors, PROFILS, TYPE_DIALOG } from '../../../../utils/constants'
import DeleteSortie from './DeleteSortieDialog'
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep'
import ClearIcon from '@material-ui/icons/Clear'
import CommentIcon from '@material-ui/icons/Comment'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { Grid, IconButton, Typography } from '@material-ui/core'
import Button from '../../../../components/Button'
import { deleteSortie, exportSortie, updateCommentaireSortie } from '../services/sortie.api'
import { useSnackbar } from '../../../../components/layout/snackbar/SnackbarContext'
import { connect } from 'react-redux'
import UpdateCommentaireSortieDialog from './UpdateCommentaireSortieDialog'
import FormButton from '../../../../components/form/FormButton'
import { hasAccess } from '../../../../components/security/AccessChecker'
import * as userSelectors from '../../../common/user/services/userSelectors'
import { UserAuthority } from '../../../common/user/services/user.types'
import ExportSortieDialog from './ExportSortieDialog'
import { checkExportStateUntilFound } from '../../../../utils/exportUtils'
import { SORTIE_EXPORT_ORIGIN } from '../services/sortie.enums'

const styles = () => getStyles({
	sectionTitle: {
		fontSize: 18
	},

	// Datatable
	table: {
		borderCollapse: 'collapse' as 'collapse',
		'& [class*="MuiTableCell-root"]': {
			height: 40
		},
		'& [class*="MuiTableCell-head"]': {
			padding: '0 5px',
			lineHeight: '1.3'
		},
		'& [class*="MuiTableCell-body"]': {
			padding: '0 5px',
			textAlign: 'center' as 'center'
		}
	},
	bold: {
		fontWeight: 600
	},
	caption: {
		color: '#88888A',
		fontSize: 10,
		fontWeight: 400,
		marginLeft: 4
	},
	centeredCell: {
		textAlign: 'center' as 'center'
	},
	commentaireCell: {
		maxWidth: 200,
		overflow: 'hidden' as 'hidden',
		textOverflow: 'ellipsis' as 'ellipsis'
	},
	actionsCell: {
		maxWidth: 50
	},
	icon: {
		width: 20,
		height: 20
	},

	// Popin
	popinTitleIcon: {
		color: colors.primary,
		marginRight: 8
	}
})

type SortieHistoryProps = {
	typeConditionnementList: ValueListEntry[]
	userAuthorities: UserAuthority[]
	refreshTable: () => void
	classes: ClassNameMap
}

const SortieHistory: FC<SortieHistoryProps> = (
	{
		typeConditionnementList,
		userAuthorities,
		refreshTable,
		classes
	}
) => {
	const { openDialog, closeDialog } = useContext(DialogContext)
	const { snackSuccess, snackError } = useSnackbar()

	const getDisplayQuantiteUc = (sortie: Sortie): QuantiteUcDisplayable => {
		const formattedTypeLabel = typeConditionnementList.find(c => c.code === sortie.typeConditionnement)?.label.toLowerCase()

		return {
			quantiteUc: sortie.quantiteUc,
			typeConditionnement: formattedTypeLabel ? `${formattedTypeLabel}`.toLowerCase() : undefined
		}
	}

	const headers = useMemo(() => {
		const result: DataTableHeader<Sortie>[] = [
			{
				key: 'culture.nom',
				name: <FormattedMessage id="sorties.labels.culture" />,
				tableHeaderColumnProps: { className: classnames(classes.bold, classes.centeredCell) },
				render: (row: Sortie) => row.culture.nom,
				sortable: true
			}, {
				key: 'quantite',
				name: <FormattedMessage id="sorties.labels.quantite" />,
				tableHeaderColumnProps: {
					className: classes.centeredCell
				},
				render: (row: Sortie) => <>
					<span className={classes.bold}>{row.quantite}</span>
					<span className={classes.caption}>kg</span>
				</>,
				sortable: true
			}, {
				key: 'quantiteUc',
				name: <FormattedMessage id="sorties.labels.uc" />,
				tableHeaderColumnProps: { className: classes.centeredCell },
				render: (row: Sortie) => {
					const display = getDisplayQuantiteUc(row)
					return <>
						<span className={classes.bold}>{display.quantiteUc}</span>
						{display.typeConditionnement && <span className={classes.caption}>{display.typeConditionnement}</span>}
					</>
				},
				sortable: true
			}, {
				key: 'dateSortie',
				name: <FormattedMessage id="sorties.labels.dateSortie" />,
				tableHeaderColumnProps: { className: classes.centeredCell },
				render: (row: Sortie) => <FormattedDate
					value={LocalDateTime.fromAPI(row.dateSortie).jsDate}
					year="numeric"
					month="numeric"
					day="numeric"
					hour="numeric"
					minute="numeric"
				/>,
				sortable: true
			}, {
				key: 'debouche.nom',
				name: <FormattedMessage id="sorties.labels.debouche" />,
				tableHeaderColumnProps: { className: classes.centeredCell },
				render: (row: Sortie) => formatDeboucheLabel(row.debouche),
				sortable: true
			},
			{
				key: 'commentaire',
				name: <FormattedMessage id="sorties.labels.commentaire" />,
				tableHeaderColumnProps: { className: classnames(classes.commentaireCell, classes.centeredCell) },
				render: (row: Sortie) => row.commentaire ?? '-',
				sortable: true
			},
			{
				key: 'author.nom',
				name: <FormattedMessage id="sorties.history.header.author" />,
				tableHeaderColumnProps: { className: classnames(classes.bold, classes.centeredCell) },
				render: (row: Sortie) => `${row.author.firstname} ${row.author.lastname}`,
				sortable: true
			}, {
				key: 'dateCreation',
				name: <FormattedMessage id="sorties.history.header.dateCreation" />,
				tableHeaderColumnProps: { className: classes.centeredCell },
				render: (row: Sortie) => <FormattedDate
					value={LocalDateTime.fromAPI(row.dateCreation).jsDate}
					year="numeric"
					month="numeric"
					day="numeric"
					hour="numeric"
					minute="numeric"
				/>,
				sortable: true
			}
		]

		if (hasAccess(PROFILS.MANAGER, userAuthorities) || hasAccess(PROFILS.CHEF, userAuthorities) || hasAccess(PROFILS.SUPERVISEUR, userAuthorities)) {
			result.push({
				key: 'actions',
				name: '',
				tableHeaderColumnProps: { className: classnames(classes.centeredCell, classes.actionsCell) },
				render: (row: Sortie) => <>
					<IconButton size="small" onClick={() => openUpdatePopin(row)}>
						<CommentIcon className={classes.icon} />
					</IconButton>
					<IconButton size="small" onClick={() => openDeletionPopin(row)}>
						<ClearIcon className={classes.icon} />
					</IconButton>
				</>
			})
		}

		return result
	}, [typeConditionnementList, classes])

	const openExportPopin = () => {
		openDialog(
			<Grid container spacing={1}>
				<OpenInNewIcon className={classes.popinTitleIcon} />
				<FormattedMessage id="sorties.export.title" />
			</Grid>,
			<ExportSortieDialog
				origin={SORTIE_EXPORT_ORIGIN.SORTIE}
				form={EXPORT_SORTIE_FORM}
				onSubmit={(values: ExportSortieForm) => exportSortie(values)
					.then(checkExportStateUntilFound(snackSuccess))
					.then(closeDialog)
					.catch(() => snackError({ id: 'sorties.export.error' }))}
			/>,
			[
				<FormButton
					type="primary"
					formName={EXPORT_SORTIE_FORM}
				>
					<FormattedMessage id="actions.export" />
				</FormButton>
			], TYPE_DIALOG.ACTION,
			'actions.close',
			undefined,
			true
		)
	}

	const openUpdatePopin = (sortie: Sortie) => {
		openDialog(
			<Grid container spacing={1}>
				<CommentIcon className={classes.popinTitleIcon} />
				<FormattedMessage id="sorties.history.update.title" />
			</Grid>,
			<UpdateCommentaireSortieDialog
				form={UPDATE_COMMENTAIRE_SORTIE_FORM}
				sortie={sortie}
				quantiteUcDisplayable={getDisplayQuantiteUc(sortie)}
				onSubmit={(values: SortieCommentaireForm) => updateCommentaireSortie(sortie.id, values.commentaire)
					.then(() => {
						refreshTable()
						closeDialog()
						snackSuccess({ id: 'sorties.history.update.success' })
					})
					.catch(() => snackError({ id: 'sorties.history.update.error' }))}
			/>,
			[
				<FormButton
					type="primary"
					formName={UPDATE_COMMENTAIRE_SORTIE_FORM}
				>
					<FormattedMessage id="actions.save" />
				</FormButton>
			], TYPE_DIALOG.ACTION,
			'actions.close'
		)
	}

	const openDeletionPopin = (sortie: Sortie) => {
		openDialog(
			<Grid container spacing={1}>
				<DeleteSweepIcon className={classes.popinTitleIcon} />
				<FormattedMessage id="sorties.history.deletion.title" />
			</Grid>,
			<DeleteSortie sortie={sortie} quantiteUcDisplayable={getDisplayQuantiteUc(sortie)} />,
			[
				<Button
					type="primary"
					onClick={() => deleteSortie(sortie.id)
						.then(() => {
							refreshTable()
							closeDialog()
							snackSuccess({ id: 'sorties.history.deletion.success' })
						})
						.catch(() => snackError({ id: 'sorties.history.deletion.error' }))
					}
				>
					<FormattedMessage id="actions.confirm" />
				</Button>
			], TYPE_DIALOG.ACTION,
			'actions.close'
		)
	}

	return <>
		<Grid item container direction="row" justify="space-between">
			<Typography variant="h1" className={classes.sectionTitle}>
				<FormattedMessage id="sorties.sections.history" />
			</Typography>
			<Button
				type="secondary"
				startIcon={<CloudUploadIcon />}
				onClick={openExportPopin}
				keepCase
			>
				<FormattedMessage id="sorties.export.button" />
			</Button>
		</Grid>
		<Grid item xs={12}>
			<SpringDataTableWithLoader
				nom={SORTIES_DATATABLE}
				apiUrl="/sorties"
				defaultPageSize={50}
				tableProps={{
					stickyHeader: true,
					className: classes.table
				}}
				noResultFragment={<><br /><br /><br /><NoResultPage /></>}
				headers={headers}
			/>
		</Grid>
	</>
}

const mapStateToProps = (state: any) => ({
	userAuthorities: userSelectors.getAuthorities(state)
})

const actions = {
	refreshTable: () => dataTableActions.refresh(SORTIES_DATATABLE)
}
export default compose<FC>(
	connect(mapStateToProps, actions),
	injectTypeConditionnementValueList,
	withStyles(styles)
)(SortieHistory)